import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { INVOICE_QUOTA_FLAG } from '../../constants/flag';
import {
  useInvoiceV2Tracking,
  type InvoicePlansEntry,
  type QuotaUpgradeOrigin,
  type QuotaSnapshot,
} from '../../hooks/useInvoiceV2Tracking';
import type { InvoicingFeature } from '../constants/planFeatures';
import { INVOICE_V2_ROUTES } from '../constants/routes';
import { isQuotaExhausted, type InvoiceQuota } from '../lib/quota';
import { getQuotaService, QUOTA_QUERY_KEY } from '../services/invoicing.service';

export interface InvoiceQuotaState {
  /** La bandera del cobro por cupo: apagada, ninguna superficie lo enseña. */
  enabled: boolean;
  quota: InvoiceQuota | undefined;
  /** La primera consulta sigue en vuelo. */
  isLoading: boolean;
  exhausted: boolean;
}

/**
 * El cupo vigente del usuario, detrás de `INVOICE_QUOTA_FLAG`.
 *
 * Falla abierto: sin respuesta (cargando, error o bandera apagada) no hay cupo y
 * nadie queda bloqueado; el 429 del timbrado sigue siendo la última palabra.
 * Sin `staleTime`: quien acaba de comprar un plan vuelve al wizard y ve su cupo
 * nuevo en la siguiente montura, sin esperar a que caduque una caché.
 */
export const useInvoiceQuota = (): InvoiceQuotaState => {
  const enabled = useFeatureIsOn(INVOICE_QUOTA_FLAG);
  const { data, isLoading } = useQuery({
    queryKey: QUOTA_QUERY_KEY,
    queryFn: getQuotaService,
    enabled,
    retry: 1,
  });
  const quota = enabled ? data : undefined;
  return {
    enabled,
    quota,
    isLoading: enabled && isLoading,
    exhausted: quota ? isQuotaExhausted(quota) : false,
  };
};

/**
 * Whether the user's plan includes a feature, from `features` of the quota. Fails open
 * like the quota: with the flag off, the query loading or failed, or a server that
 * does not send `features`, everything is available. The 403 of the action stays the
 * last word.
 */
export const usePlanFeatures = () => {
  const { quota } = useInvoiceQuota();
  const features = quota?.features;
  return {
    has: (feature: InvoicingFeature) => !features || features.includes(feature),
  };
};

/** Lo que la pantalla de planes recibe por `state`: desde qué superficie se llegó, y la función que se buscaba. */
export interface InvoicePlansNavigationState {
  plansEntry?: InvoicePlansEntry;
  feature?: string;
}

/**
 * The way to the plans screen from the wizard paywall, the plan sheet of the home,
 * the stamping error or the upsell of a feature: records the tap with the quota the
 * user saw (and the feature, from the upsell) and opens it.
 */
export const useOpenInvoicePlans = () => {
  const navigate = useNavigate();
  const { trackPlanUpgradeClicked } = useInvoiceV2Tracking();
  return (origin: Exclude<QuotaUpgradeOrigin, 'plans'>, snapshot: QuotaSnapshot, feature?: string) => {
    trackPlanUpgradeClicked({ ...snapshot, origin, feature });
    const state: InvoicePlansNavigationState = { plansEntry: origin, ...(feature ? { feature } : {}) };
    navigate(INVOICE_V2_ROUTES.PLANS, { state });
  };
};
